import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Eye, EyeOff, Lock, Quote } from 'lucide-react';
import axios from 'axios';

const SAMIDEL_LOGO = "https://customer-assets.emergentagent.com/job_fabriflow/artifacts/vi8gu83e_logo-hero-samidel.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [requiresPassword, setRequiresPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fraseMotivacional, setFraseMotivacional] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();
  const { subscribe: subscribePush } = usePushNotifications(null);

  // Carregar frase motivacional aleatória
  useEffect(() => {
    const carregarFrase = async () => {
      try {
        const response = await axios.get(`${API}/configuracoes/frases-motivacionais/ativas?contexto=login`);
        if (response.data && response.data.length > 0) {
          const fraseAleatoria = response.data[Math.floor(Math.random() * response.data.length)];
          setFraseMotivacional(fraseAleatoria.texto);
        }
      } catch (error) {
        console.log('Sem frases motivacionais disponíveis');
      }
    };
    carregarFrase();
  }, []);

  // Verificar se o utilizador requer senha (gestor)
  const [checkingUser, setCheckingUser] = useState(false);
  
  // Debounce para verificar o utilizador
  useEffect(() => {
    if (!username.trim()) {
      setRequiresPassword(false);
      return;
    }
    
    const timeoutId = setTimeout(async () => {
      setCheckingUser(true);
      try {
        const response = await axios.get(`${API}/check-user/${encodeURIComponent(username.trim())}`);
        setRequiresPassword(response.data.requires_password === true);
      } catch (error) {
        // Se o utilizador não existe, não requer senha (será tratado no submit)
        setRequiresPassword(false);
      } finally {
        setCheckingUser(false);
      }
    }, 300); // Debounce de 300ms
    
    return () => clearTimeout(timeoutId);
  }, [username]);
  
  const handleUsernameChange = (e) => {
    setUsername(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim()) {
      toast.error('Por favor, insira o seu nome de utilizador');
      return;
    }

    if (requiresPassword && !password.trim()) {
      toast.error('Por favor, insira a sua senha');
      return;
    }

    setLoading(true);
    try {
      const user = await login(username, requiresPassword ? password : null);
      
      // Formatar nome para saudação (primeiro e último nome, capitalizado)
      const formatarNome = (nome) => {
        if (!nome) return '';
        const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
        const partes = nome.split(' ').filter(n => n).map(n => capitalize(n));
        if (partes.length >= 2) {
          return `${partes[0]} ${partes[partes.length - 1]}`;
        }
        return partes[0] || '';
      };
      
      const nomeFormatado = formatarNome(user.nome_completo || user.nome) || formatarNome(user.username?.replace(/\./g, ' ')) || user.username;
      toast.success(`Bem-vindo, ${nomeFormatado}!`);
      
      const role = user.role || user.perfil;
      
      // Subscrever push notifications silenciosamente via SW unificado
      if ('Notification' in window && Notification.permission !== 'denied') {
        try {
          if ('serviceWorker' in navigator && 'PushManager' in window) {
            navigator.serviceWorker.ready.then(async (reg) => {
              const permission = await Notification.requestPermission();
              if (permission === 'granted') {
                const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
                if (vapidKey) {
                  function urlBase64ToUint8Array(base64String) {
                    const padding = '='.repeat((4 - base64String.length % 4) % 4);
                    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
                    const rawData = window.atob(base64);
                    const arr = new Uint8Array(rawData.length);
                    for (let i = 0; i < rawData.length; ++i) arr[i] = rawData.charCodeAt(i);
                    return arr;
                  }
                  const sub = await reg.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: urlBase64ToUint8Array(vapidKey)
                  });
                  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
                  const axios = (await import('axios')).default;
                  await axios.post(`${API}/push/subscribe`, {
                    user_id: user.id,
                    subscription: sub.toJSON()
                  });
                }
              }
            }).catch(() => {});
          }
        } catch {}
      }

      if (role === 'admin' || role === 'gestor') {
        navigate('/gestor/dashboard');
      } else {
        navigate('/operador');
      }
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error(error.response.data?.detail || 'Conta bloqueada. Tente mais tarde.');
      } else if (error.response?.status === 401) {
        toast.error('Senha incorreta');
      } else if (error.response?.status === 404) {
        toast.error('Utilizador não encontrado');
      } else {
        toast.error('Erro ao fazer login. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#1a1a2e] relative overflow-hidden">
        {/* Geometric Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center items-center w-full p-12 text-white">
          <img 
            src={SAMIDEL_LOGO} 
            alt="Samidel" 
            className="h-24 mb-8"
          />
          <div className="text-center max-w-md">
            <p className="text-xl font-light text-white/80 mb-4">
              Sistema de Gestão de Produção
            </p>
            <div className="flex items-center justify-center gap-4 text-white/60 text-sm mb-8">
              <span>Eficiência</span>
              <span className="w-1 h-1 rounded-full bg-white/40"></span>
              <span>Controlo</span>
              <span className="w-1 h-1 rounded-full bg-white/40"></span>
              <span>Qualidade</span>
            </div>
            
            {/* Frase Motivacional */}
            {fraseMotivacional && (
              <div className="mt-8 p-6 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20">
                <Quote className="h-8 w-8 text-[#E07A5F] mb-4 mx-auto opacity-80" />
                <p className="text-lg italic text-white/90 leading-relaxed">
                  "{fraseMotivacional}"
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-[#E07A5F]/20 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-[#81B29A]/20 to-transparent rounded-full blur-3xl"></div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-stone-50">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="inline-block bg-[#1a1a2e] p-4 rounded-2xl mb-4">
              <img 
                src={SAMIDEL_LOGO} 
                alt="Samidel" 
                className="h-12"
              />
            </div>
          </div>

          <div className="text-center mb-8">
            <h2 className="text-3xl font-heading font-bold text-stone-900 mb-2">Bem-vindo</h2>
            <p className="text-stone-600">Insira o seu nome de utilizador para continuar</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-stone-700 mb-2">
                Nome de Utilizador
              </label>
              <Input
                id="username"
                type="text"
                placeholder="ex: maria.silva"
                value={username}
                onChange={handleUsernameChange}
                className="h-12"
                data-testid="username-input"
                autoFocus
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck="false"
              />
            </div>

            {/* Campo de senha - apenas para gestor */}
            {requiresPassword && (
              <div className="animate-in fade-in slide-in-from-top-2 duration-300 pt-2">
                <label htmlFor="password" className="block text-sm font-medium text-stone-700 mb-2">
                  <Lock className="h-4 w-4 inline mr-1" />
                  Senha
                </label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Insira a sua senha"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-12 pr-12"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                <p className="text-xs text-stone-500 mt-2">
                  O acesso de gestor requer autenticação com senha
                </p>
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-12 rounded-full text-base font-semibold bg-[#1a1a2e] hover:bg-[#2a2a3e]"
              disabled={loading}
              data-testid="login-button"
            >
              {loading ? 'A entrar...' : 'Entrar'}
            </Button>
          </form>

          <div className="mt-8 p-4 bg-white rounded-xl border border-stone-200">
            <p className="text-xs text-stone-500 mb-2 font-medium">Exemplos de login:</p>
            <div className="space-y-1 text-xs text-stone-600">
              <p>• <code className="bg-stone-100 px-2 py-0.5 rounded">gestor</code> - Acesso de Gestor (requer senha)</p>
              <p>• Qualquer username de operador (sem senha)</p>
            </div>
          </div>

          <p className="text-center text-xs text-stone-400 mt-6">
            Samidel Textile Manufacturing © 1987
          </p>
        </div>
      </div>
    </div>
  );
}
